import Konva from 'konva'
import { currFormula, pinToSafeInt } from './Formulas'
import { Config } from './types'

export type Point3D = {
  x: number
  y: number
  z: number
}

export type Point2D = {
  x: number
  y: number
}

export const rotateX = (point: Point3D, angle: number): Point3D => {
  const cosA = Math.cos(angle)
  const sinA = Math.sin(angle)
  return {
    x: point.x,
    y: point.y * cosA - point.z * sinA,
    z: point.y * sinA + point.z * cosA,
  }
}

export const rotateY = (point: Point3D, angle: number): Point3D => {
  const cosA = Math.cos(angle)
  const sinA = Math.sin(angle)
  return {
    x: point.x * cosA + point.z * sinA,
    y: point.y,
    z: -point.x * sinA + point.z * cosA,
  }
}

export const rotateZ = (point: Point3D, angle: number): Point3D => {
  const cosA = Math.cos(angle)
  const sinA = Math.sin(angle)
  return {
    x: point.x * cosA - point.y * sinA,
    y: point.x * sinA + point.y * cosA,
    z: point.z,
  }
}

export const rotatePoint = (point: Point3D, angle: Point3D): Point3D => {
  let rotatedPoint = rotateX(point, angle.x)
  rotatedPoint = rotateY(rotatedPoint, angle.y)
  rotatedPoint = rotateZ(rotatedPoint, angle.z)
  return rotatedPoint
}
export const projectPoint3D = (point: Point3D, frustum: Point3D): Point2D => {
  const distance = frustum.z // Distance from the "camera"
  const scale = distance / (distance - point.z) // Perspective projection
  const projectedX = point.x * scale + frustum.x / 2
  const projectedY = -point.y * scale + frustum.y / 2 // Invert Y to match canvas orientation
  return { x: projectedX, y: projectedY }
}
/*
  - image must be invariant at any image width, i.e. actual zoom (and pan) must
  be calibrated to image width.
  - choice of clockwise/counter-clockwise and flip direction is arbitrary, in
   that there are only eight distinct orientations (pure reflection or rotation into each quadrant), so:
    - default to ROTATE_CCW and REFLECT_HORIZ
    - // TODO: support user-selected anyway, which i guess unlocks arbitrary perspective support
 */

const REFERENCE_WIDTH = 100
type PlotParams = { width: number; config: Config; isLog: boolean }

export const getDrawItV1 = ({ width, config, isLog }: PlotParams) => {
  const {
    step,
    progress: thetaRange,
    zoom,
    vert,
    horiz,
    rotZ: rotationDegrees,
    cycles,
    isFlip,
  } = config
  const rotationRads = (-rotationDegrees * Math.PI) / 180
  const plotter = currFormula(config)
  const { isPolar, isWantRadians } = plotter
  const label = plotter.label()
  const actualZoom = (zoom * plotter.scale() * width) / REFERENCE_WIDTH
  const offsetFactor = plotter.offset()
  const offsetX = width / 2 - offsetFactor.x * actualZoom + horiz
  const offsetY = width / 2 - offsetFactor.y * actualZoom + vert
  const actualOffsetX = offsetFactor.x * actualZoom
  const actualOffsetY = offsetFactor.y * actualZoom
  const flipFactor = { x: 1, y: isFlip ? -1 : 1 }
  const rotate = (x: number, y: number, radians: number) => {
    const cos = Math.cos(radians)
    const sin = Math.sin(radians)
    const nx = cos * (x - actualOffsetX) + sin * (y - actualOffsetY) + actualOffsetX
    const ny = cos * (y - actualOffsetY) - sin * (x - actualOffsetX) + actualOffsetY
    return { x: nx, y: ny }
  }
  const reflect = (x: number, y: number) => {
    return {
      x: flipFactor.x * x,
      y: flipFactor.y * y,
    }
  }
  const polarPlot = (r: number, theta: number) => {
    const rotatedX = actualZoom * r * Math.cos(theta + rotationRads)
    const rotatedY = actualZoom * r * Math.sin(theta + rotationRads)
    return reflect(rotatedX, rotatedY)
  }
  const xyPlot = (r: number, degrees: number) => {
    const x = actualZoom * (degrees % 360)
    const y = actualZoom * r
    const { x: rotatedX, y: rotatedY } = rotate(x, y, rotationRads)
    return reflect(rotatedX, rotatedY)
  }
  const plotFunc = isPolar ? polarPlot : xyPlot
  const drawIt = (context: Konva.Context) => {
    if (isLog) {
      console.info(
        `[degrees (theta), r] [x,y] for: ${label} -- step: ${step} zoom: ${zoom} offset: ${offsetX},${offsetY}`
      )
      console.info(`formula: ${plotter.formula}`)
    }
    const degreeStep = step / Math.max(1, cycles)
    const degreeRange = step * thetaRange
    for (let degrees = 0; degrees < degreeRange; degrees += degreeStep) {
      const theta = (degrees * Math.PI) / 180
      const r = plotter.formula(isWantRadians ? theta : degrees)
      const { x, y } = plotFunc(r, isPolar ? theta : degrees % 360)
      if (isLog) {
        console.info(`[${degrees} (${theta}), ${r}] [${x},${y}]`)
      }
      const targetX = pinToSafeInt(x + offsetX)
      const targetY = pinToSafeInt(y + offsetY)
      if (degrees === 0) {
        context.moveTo(targetX, targetY)
      } else {
        context.lineTo(targetX, targetY)
      }
    }
  }
  return drawIt
}
export const getDrawItV2 = ({ width, config, isLog }: PlotParams) => {
  const {
    step,
    progress: thetaRange,
    zoom,
    vert,
    horiz,
    rotX: rotXDegrees,
    rotY: rotYDegrees,
    rotZ: rotZDegrees,
    frustum,
    cycles,
    isFlip,
  } = config
  const rotationRads = {
    x: (-rotXDegrees * Math.PI) / 180,
    y: (-rotYDegrees * Math.PI) / 180,
    z: (-rotZDegrees * Math.PI) / 180,
  }
  const plotter = currFormula(config)
  const { isPolar, isWantRadians } = plotter
  const label = plotter.label()
  const actualZoom = (zoom * plotter.scale() * width) / REFERENCE_WIDTH
  const offsetFactor = plotter.offset()
  const offsetX = isPolar ? horiz : -offsetFactor.x * actualZoom + horiz
  const offsetY = isPolar ? vert : -offsetFactor.y * actualZoom + vert
  const flipFactor = { x: 1, y: isFlip ? -1 : 1 }
  const reflect = (x: number, y: number) => {
    return {
      x: flipFactor.x * x,
      y: flipFactor.y * y,
    }
  }
  const projectionFrustum = { x: width, y: width, z: frustum }

  const polarPlot = (r: number, theta: number) => {
    const x = actualZoom * r * Math.cos(theta)
    const y = actualZoom * r * Math.sin(theta)
    const rotated = rotatePoint({ x, y, z: 0 }, rotationRads)
    const projected = projectPoint3D(rotated, projectionFrustum)
    return reflect(projected.x, projected.y)
  }
  const xyPlot = (r: number, degrees: number) => {
    const x = actualZoom * (degrees % 360)
    const y = actualZoom * r
    const rotated = rotatePoint({ x, y, z: 0 }, rotationRads)
    const projected = projectPoint3D(rotated, projectionFrustum)
    return reflect(projected.x, projected.y)
  }
  const plotFunc = isPolar ? polarPlot : xyPlot
  const drawIt = (context: Konva.Context) => {
    if (isLog) {
      console.info(
        `[degrees (theta), r] [x,y] for: ${label} -- step: ${step} zoom: ${zoom} offset: ${offsetX},${offsetY}`
      )
      console.info(`formula: ${plotter.formula}`)
    }
    const degreeStep = step / Math.max(1, cycles)
    const degreeRange = step * thetaRange
    for (let degrees = 0; degrees < degreeRange; degrees += degreeStep) {
      const theta = (degrees * Math.PI) / 180
      const r = plotter.formula(isWantRadians ? theta : degrees)
      const { x, y } = plotFunc(r, isPolar ? theta : degrees % 360)
      if (isLog) {
        console.info(`[${degrees} (${theta}), ${r}] [${x},${y}]`)
      }
      const targetX = pinToSafeInt(x + offsetX)
      const targetY = pinToSafeInt(y + offsetY)
      if (degrees === 0) {
        context.moveTo(targetX, targetY)
      } else {
        context.lineTo(targetX, targetY)
      }
    }
  }

  return drawIt
}
